import React from 'react';
import { AlertCircle, CheckCircle2, Loader2, Sparkles } from 'lucide-react';

const STAGE_DEFINITIONS = [
  { id: 'profile_sync', label: 'Profile sync', subtitle: 'Importing your Upwork profile' },
  { id: 'job_filters', label: 'Smart filters', subtitle: 'Tailoring your job feed' },
  { id: 'job_sync', label: 'Job feed', subtitle: 'Pulling matching jobs' },
  { id: 'first_proposal', label: 'First draft', subtitle: 'Drafting a starter proposal' },
];

function getStageStyles(state) {
  switch (state) {
    case 'completed':
      return {
        chip: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
      };
    case 'running':
      return {
        chip: 'border-primary-200 bg-primary-50 text-primary-700 shadow-sm shadow-primary-100/60',
        icon: <Loader2 className="w-4 h-4 animate-spin text-primary-600" />,
      };
    case 'failed':
      return {
        chip: 'border-rose-200 bg-rose-50 text-rose-700',
        icon: <AlertCircle className="w-4 h-4 text-rose-600" />,
      };
    case 'skipped':
      return {
        chip: 'border-slate-200 bg-slate-50 text-slate-600',
        icon: <span className="w-2 h-2 rounded-full bg-slate-400" />,
      };
    default:
      return {
        chip: 'border-white/70 bg-white/70 text-slate-500',
        icon: <span className="w-2 h-2 rounded-full bg-slate-300" />,
      };
  }
}

function buildHeadline(accessEnabled, activation) {
  if (!accessEnabled) {
    return {
      badge: 'Connect Upwork to get started',
      title: 'Propel handles the rest once you connect',
      description: 'Link your Upwork account and we\'ll set up your filters, sync your profile, pull matching jobs, and draft your first proposal.',
    };
  }

  if (activation?.canAutoActivate === false) {
    return {
      badge: 'Browser-Assist mode active',
      title: 'You\'re set up for extension-first workflow',
      description: 'Use the Propel extension to qualify jobs and draft proposals directly on Upwork. Full feed sync is available via OAuth.',
    };
  }

  if (activation?.state === 'running') {
    const currentStage = activation.stages?.find((stage) => stage.state === 'running');
    return {
      badge: `${activation.progress?.doneCount || 0} of ${activation.progress?.total || 4} automations done`,
      title: 'Setting up your workspace',
      description: currentStage?.detail || 'Syncing your profile, configuring filters, and pulling matching jobs.',
    };
  }

  if (activation?.state === 'completed_with_issues') {
    return {
      badge: 'Setup finished — 1 item needs attention',
      title: 'Almost there',
      description: activation.error || activation.message || 'Setup completed but one step needs a manual fix.',
    };
  }

  if (activation?.state === 'completed') {
    return {
      badge: 'Setup complete',
      title: 'Your workspace is ready',
      description: activation.result?.firstProposalTitle
        ? `Your feed is live and a draft for "${activation.result.firstProposalTitle}" is ready to review.`
        : 'Your filters and feed are live. We\'ll surface your best matches.',
    };
  }

  return {
    badge: 'Ready to auto-configure',
    title: 'Propel will handle the next steps',
    description: 'Once your Upwork connection is confirmed, setup runs automatically.',
  };
}

export function OnboardingActivationPanel({
  accessEnabled,
  activation,
  stepsCount,
  completedCount,
}) {
  const stageMap = new Map((activation?.stages || []).map((stage) => [stage.id, stage]));
  const stages = STAGE_DEFINITIONS.map((stage) => ({
    ...stage,
    ...(stageMap.get(stage.id) || {}),
  }));

  const stepProgress = stepsCount > 0 ? Math.round((completedCount / stepsCount) * 100) : 0;
  const progressPercent = activation?.progress?.percent ?? stepProgress;
  const progressLabel = activation?.progress
    ? `${activation.progress.doneCount} of ${activation.progress.total} automations done`
    : `${completedCount} of ${stepsCount} onboarding steps complete`;
  const headline = buildHeadline(accessEnabled, activation);

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-white/80 bg-white/85 shadow-[0_30px_80px_-45px_rgba(15,23,42,0.45)] backdrop-blur">
      <div className="absolute -left-20 top-10 h-40 w-40 rounded-full bg-primary-200/50 blur-3xl animate-onboarding-float" />
      <div className="absolute -right-12 top-6 h-32 w-32 rounded-full bg-sky-200/60 blur-3xl animate-onboarding-float-delayed" />

      <div className="relative px-6 py-6 sm:px-8">
        <div className="flex items-start justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary-200/70 bg-primary-50/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary-700">
              <Sparkles className="w-3.5 h-3.5" />
              {headline.badge}
            </div>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]">
              {headline.title}
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600 sm:text-base">
              {headline.description}
            </p>
          </div>

          <div className="hidden rounded-2xl border border-white/70 bg-white/80 px-4 py-3 shadow-sm sm:block">
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500">Progress</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{progressPercent}%</p>
            <p className="mt-1 text-xs text-slate-500">{progressLabel}</p>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between text-xs font-medium uppercase tracking-[0.14em] text-slate-500">
            <span>Setup progress</span>
            <span>{progressLabel}</span>
          </div>
          <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100">
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPercent}
              aria-label="Automatic onboarding progress"
              className="relative h-full rounded-full bg-gradient-to-r from-primary-600 via-sky-500 to-primary-400 transition-[width] duration-700 ease-out"
              style={{ width: `${progressPercent}%` }}
            >
              {activation?.state === 'running' && (
                <div className="absolute inset-y-0 left-[-20%] w-1/3 bg-white/35 blur-xl animate-onboarding-sheen" />
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {stages.map((stage) => {
            const styles = getStageStyles(stage.state);

            return (
              <div
                key={stage.id}
                className={`rounded-2xl border px-4 py-4 transition-all ${styles.chip}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{stage.label}</p>
                    <p className="mt-1 text-xs opacity-80">{stage.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0">{styles.icon}</div>
                </div>
                {(stage.detail || stage.error) && (
                  <p className="mt-3 text-xs leading-5 opacity-90">
                    {stage.error || stage.detail}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default OnboardingActivationPanel;
